import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import {
  Box,
  Card,
  CardContent,
  IconButton,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useLocations } from './model';

/** Signed quantity with + / − and a meaning colour. */
export function SignedQty({ value, unit }: { value: string | number; unit?: string }) {
  const n = Number(value);
  const text = `${n > 0 ? '+' : n < 0 ? '−' : ''}${Math.abs(n).toLocaleString(undefined, {
    maximumFractionDigits: 4,
  })}${unit ? ` ${unit}` : ''}`;
  return (
    <Box
      component="span"
      className="tabular"
      sx={{
        fontWeight: 650,
        whiteSpace: 'nowrap',
        color: n > 0 ? 'success.main' : n < 0 ? 'error.main' : 'text.secondary',
      }}
    >
      {text}
    </Box>
  );
}

/** Small metric tile used across the inventory screens. */
export function StatCard({
  label,
  value,
  hint,
  icon,
  tone = 'primary',
  loading,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  icon: ReactNode;
  tone?: 'primary' | 'success' | 'warning' | 'error' | 'info';
  loading?: boolean;
}) {
  return (
    <Card sx={{ position: 'relative', overflow: 'hidden' }}>
      <Box
        sx={(t) => {
          const c = (t.vars ?? t).palette[tone].main;
          return {
            position: 'absolute',
            right: -28,
            top: -28,
            width: 110,
            height: 110,
            borderRadius: '50%',
            background: `radial-gradient(circle, color-mix(in srgb, ${c} 16%, transparent), transparent 70%)`,
          };
        }}
      />
      <CardContent>
        <Stack direction="row" sx={{ alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 550 }}>
            {label}
          </Typography>
          <Box
            sx={(t) => {
              const c = (t.vars ?? t).palette[tone].main;
              return {
                width: 36,
                height: 36,
                borderRadius: '11px',
                display: 'grid',
                placeItems: 'center',
                color: c,
                bgcolor: `color-mix(in srgb, ${c} 10%, transparent)`,
                '& svg': { fontSize: 20 },
              };
            }}
          >
            {icon}
          </Box>
        </Stack>
        {loading ? (
          <Skeleton width="55%" height={40} />
        ) : (
          <Typography variant="h4" className="tabular" sx={{ mt: 0.5 }}>
            {value}
          </Typography>
        )}
        {hint !== undefined && (
          <Typography variant="caption" color="text.secondary">
            {loading ? <Skeleton width="40%" /> : hint}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export function StatGrid({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
        mb: 2.5,
        '& > *': { minWidth: 0 },
      }}
    >
      {children}
    </Box>
  );
}

/** Location filter / field. `allLabel` adds an "all locations" option with an empty value. */
export function LocationSelect({
  value,
  onChange,
  allLabel,
  label = 'Location',
  onlyActive = true,
  error,
  helperText,
  size,
  disabled,
}: {
  value: string;
  onChange: (id: string) => void;
  allLabel?: string;
  label?: string;
  onlyActive?: boolean;
  error?: boolean;
  helperText?: string;
  size?: 'small' | 'medium';
  disabled?: boolean;
}) {
  const { data: locations } = useLocations();
  const list = (locations ?? []).filter((l) => !onlyActive || l.isActive || l.id === value);
  return (
    <TextField
      select
      label={label}
      value={locations ? value : ''}
      onChange={(e) => onChange(e.target.value)}
      error={error}
      helperText={helperText}
      size={size}
      disabled={disabled}
      sx={{ minWidth: 180, maxWidth: { sm: 280 } }}
    >
      {allLabel !== undefined && <MenuItem value="">{allLabel}</MenuItem>}
      {list.map((l) => (
        <MenuItem key={l.id} value={l.id}>
          {l.name}
          {l.isDefault && (
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              default
            </Typography>
          )}
        </MenuItem>
      ))}
    </TextField>
  );
}

/** Title row for detail pages: back button, title, chips and actions. */
export function DetailHeader({
  back,
  title,
  chips,
  subtitle,
  actions,
}: {
  back: string;
  title: string;
  chips?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  const navigate = useNavigate();
  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={2}
      sx={{ alignItems: { md: 'center' }, justifyContent: 'space-between', mb: 3 }}
    >
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', minWidth: 0 }}>
        <Tooltip title="Back">
          <IconButton
            aria-label="Back"
            onClick={() => void navigate(back)}
            sx={{ border: 1, borderColor: 'divider', bgcolor: 'background.paper' }}
          >
            <ArrowBackRounded />
          </IconButton>
        </Tooltip>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="h3" noWrap>
              {title}
            </Typography>
            {chips}
          </Stack>
          {subtitle && (
            <Typography color="text.secondary" sx={{ mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Stack>
      {actions && (
        <Stack direction="row" spacing={1.25} sx={{ flexWrap: 'wrap', rowGap: 1.25 }}>
          {actions}
        </Stack>
      )}
    </Stack>
  );
}
